/**
 * Background:
 * Developer 'owns' the suggestion
 * Builder 'owns' the pricing (and cannot see anyone else's pricing)
 *
 * interface VEListItem {
 *     suggestion: VESuggestion;
 *     prices: VEPricing[];
 * }
 *
 * There is 0 or 1 VE Prices per bidder.
 *
 * The goal here is to show Developer a view like so:
 *             bidder 1             bidder 2
 * suggestion1, desc    |    amount, note    |    amount, note   ...etc
 * sugegstion2, desc    |    <empty>        |     amount, note   ... etc
 *
 *
 * There can be a few hundred 'live' VE items per project.
 * so with a reasonable upper limit of 10 bidders we get a grid with 1000 data points.
 *
 *
 *
 * additional functionality:
 *  this will need to be able ot export to excel at some point.
 *
 *
 * (The Builder Version of this will be simple <suggestion,price> table)
 *
 *  */
import {useAuthInfo} from "@propelauth/react";
import {Link, useParams} from "react-router-dom";
import React, {useEffect, useState} from "react";

import {VEListItem, VEMatrix, VEPricing, VESuggestion} from "./ve_model";
import {CardContent, CardHeader, CardTitle} from "../ui/card";
import {Button} from "../ui/button";
import {ArrowUpDown, PencilLine} from "lucide-react";
import VeItemDeveloperView from "./VeItemDeveloper";
import {UserContext} from "../../contexts/UserContext";
import {
    DotsHorizontalIcon,
    Pencil1Icon,
    Share1Icon,
    CheckCircledIcon,
    CheckIcon,
    CircleIcon,
} from "@radix-ui/react-icons";
import {CompanyBase} from "@models/company";
import {fetchWithToken, putWithToken} from "../../api/apiBase";
import {SortableHeader} from "../ui/tableUtils";
import {Tooltip, TooltipContent, TooltipProvider, TooltipTrigger} from "../ui/tooltip";
import VeItemForm from "./veItemView";
import {useToast} from "../ui/use-toast";
import ScrollTable, { Column } from "../ui/ScrollableTable";
import {moneyFormatter} from "../../lib/formatters";
import {CopyButton2} from "../exportData/CopyButton";

const descriptions = {
    developer: {"enable sharing": "This will ... "},
};


type CompanyColumnProps = {
    company: CompanyBase;
    column: any;
};


function CompanyColumn({company, column}: CompanyColumnProps) {
    return (
        <Button
            className="text-wrap text-xs"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
            {company.name}
            <ArrowUpDown className="ml-2 h-4 w-4"/>
        </Button>
    );
}

/**
 * DEVELOPER ONLY
 *
 * we load a preformatted matrix shaped:
 * columns: [{company_id: xx, name: } {company_id: xy, name:NAME2} {company_id: xz, name:NAME3}
 * rows : [
 *     {suggestion: {..Suggestion},  xx: {...Price}, xy: {...Price_xy}, xz: { empty } }
 * ]
 * now the datatable can look up
 *
 * */
function DeveloperVeView() {
    const {project_id} = useParams<string>();
    const {accessToken} = useAuthInfo()
    const [isEditing, setIsEditing] = React.useState<boolean>(false);
    const {toast} = useToast();
    const [suggestionId, setSuggestionId] = React.useState<string | null>(null);
    const [priceId, setPriceId] = React.useState<string | null>(null);
    const [editingItem, setEditingItem] = React.useState<VEListItem | null>(null);

    const userContext = React.useContext(UserContext);
    const [tableCols, setTableColumns] = useState<any[]>([])
    const [matItems, setMatItems] = React.useState<VEMatrix | null>(null);
    const [showDetail, setShowDetail] = React.useState<boolean>(false)

    useEffect(() => {
        if (project_id && accessToken) {
            fetchWithToken(`/projects/${project_id}/developer/ve/`, accessToken)
                .then((data) => {
                    // console.log(data);
                    setMatItems(data);
                    const tableCols = buildCols(data);
                    setTableColumns(tableCols);
                })
                .catch((err) => {
                    console.log("Error:", err);
                });
        } else {

        }
    }, [project_id]);

    const onSuggestVE = (e: any) => {
        e.preventDefault();
        setIsEditing(true);
    };

    const onCloseVE = () => {
        setIsEditing(false);
        setSuggestionId(null);
    };

    const onOpenItem = (suggestion: VESuggestion, price: any) => {

        setSuggestionId(suggestion.id);
        if(price) {
            setPriceId(price._id);
            setEditingItem({price: {...price, id: price._id}, suggestion: suggestion})
        } else {
            setEditingItem({price: null, suggestion: suggestion})
        }
        setIsEditing(true);
    };

    const onOpenRow = (suggestion: VESuggestion) => {
        setSuggestionId(suggestion.id);
        setPriceId(null);
        setEditingItem({price: null, suggestion: suggestion})
        setIsEditing(true);
    };

    const onShowDetail = () => {
        setShowDetail((val) => true);
    }

    const shareSuggestion = (suggestion_id: string) => {
        if (accessToken) {
            putWithToken(
                `/projects/${project_id}/ve/${suggestion_id}/share`,
                null,
                accessToken)
                .then((res: VESuggestion) => {
                    console.log(res)
                    
                    setMatItems((prevState) => {
                        const updatedMatItems: VEMatrix = {
                            ...prevState,
                            rows: prevState!.rows.map((row) => {
                                if (row.suggestion?.id === suggestion_id) {
                                    return {
                                        ...row,
                                        suggestion: res,
                                    };
                                }
                                return row;
                            }),
                            columns: prevState?.columns ?? [],
                        };
                        return updatedMatItems; 
                    });
                
                    onUpdateListItem({price: null, suggestion: res});

                    toast({
                        title: "Success!",
                        description: "VE item shared.",
                        variant: "default",
                    });
                }).catch(err => {
                    toast({variant: "destructive", title: "Error", description: err || "CouldShare",});
            })
        }
    }

    const insertListItem = (row: VEListItem) => {
        if (!matItems) { return }
        const newLine = {suggestion: row.suggestion};
        setMatItems({rows: [... matItems.rows, newLine], columns: matItems.columns});
    }

    const hideListItem = (row: VEListItem) => {
        if (!matItems) { return }
        const newItems = matItems.rows.filter((item) => {
            if (item.suggestion.id !== row.suggestion.id) {
                return item;
            }
        });
        setMatItems({rows: newItems, columns: matItems.columns});
        onCloseVE();
    }

    const onUpdateListItem = (row: VEListItem) => {
        if (!matItems) {   return  }
        const newItems = matItems.rows.map((item) => {
            if (item.suggestion.id === row.suggestion.id) {
                return {...item, suggestion: row.suggestion};
            } else {
                return item;
            }
        });
        setMatItems({rows: newItems, columns: matItems.columns});
    };

    const buildCols = (matrixData: any) => {
        /** Build standard columns  **/
        var arr: any = [
            {
                id: "Actions",
                header: "",
                cell: (row: any) => {
                    return (
                        <div className="flex">
                            {/*share button -> Send Share REquest  */}
                            <Button
                                variant="outline" className="mr-5"
                                size="sm"
                                onClick={() => onOpenItem(row.original.suggestion, null)}
                            >
                                <Pencil1Icon className="h-4 w-4"/>
                            </Button>

                            {row.original.suggestion.shared_state?.share_enabled === "allowed"
                                ? (<CheckCircledIcon className="mr-5" width={17} height={17}/>)
                                : (
                                    <TooltipProvider>
                                        <Tooltip>
                                            <TooltipTrigger>
                                                <a className="mr-5"
                                                   onClick={(e) => {
                                                       shareSuggestion(row.original.suggestion.id)
                                                   }}>

                                                    <Share1Icon width={17} height={17}></Share1Icon>
                                                </a>
                                            </TooltipTrigger>
                                            <TooltipContent>
                                                <p>Share VE idea with other bidders</p>
                                            </TooltipContent>
                                        </Tooltip>
                                    </TooltipProvider>
                                )}
                        </div>
                    );
                },
            },
            {
                header: ({column}: any) => (<SortableHeader column={column} name={"Suggestion"}/>),
                accessorKey: "suggestion.title",
            },
            {
                header: ({column}: any) => (<SortableHeader column={column} name={"Description"}/>),
                accessorKey: "suggestion.description",
            },
        ];

        /*
        * Build
        * */
        if (matrixData) {
            for (let i = 0; i < matrixData.columns.length; i++) {
                /**
                 * Each company gets a column
                 * The company_id becomes the column key, and we lookup cell data based on that
                 *
                 * */
                const company_key = matrixData.columns[i].company_id;

                const rowDef = {

                    key: company_key,
                    accessorKey: company_key + ".savings_amount",

                    header: ({column}: any) => (
                        <CompanyColumn
                            column={column}
                            company={matrixData.columns[i].company}/>
                    ),
                    // cell
                    cell: (row: any) => {
                        if (!row.original[company_key]){
                            return <></>;
                        }
                        const amount = row.original[company_key]["savings_amount"];

                        if (!amount) {
                            return <></>;
                        } else {

                            const price_item = row.original[company_key];
                            const op = row.original.suggestion.created_by.company.id;

                            return (
                                <>
                                    <button className="flex"
                                            onClick={(e) => onOpenItem(row.original.suggestion, price_item)}>

                                        {/*The amount*/}
                                        <span className="mr-3">{moneyFormatter.format(amount)}</span>

                                        {/*Indicate whether bidder in this col suggested this item */}
                                        {op === company_key && <CheckIcon className="mr-5" width={17} height={17}/>}


                                    </button>
                                    {showDetail ?
                                        <div>{price_item.description}</div>
                                        : <></>}
                                </>
                            );
                        }
                    },
                };

                arr.push(rowDef);
            }
        }
        // console.log('arr', arr)
        return arr;
    };

    const formatDataToExport = () => {
        if (!matItems?.rows) return [];
        const data = matItems?.rows.map(item => ({
            suggestion: item.suggestion.title,
            description: item.suggestion.description,
            TOCCI: item["6689e153d07f8e6f5673c593"].savings_amount ? moneyFormatter.format(item["6689e153d07f8e6f5673c593"].savings_amount) : '',
            bigBobBuilding: item["66ae759f8d025ce13bb2f642"].savings_amount ? moneyFormatter.format(item["66ae759f8d025ce13bb2f642"].savings_amount) : '',
            JMKContacting: item["66d61dd7fcb67af0aab341a4"].savings_amount ? moneyFormatter.format(item["66d61dd7fcb67af0aab341a4"].savings_amount) : '',
            theGreenBuilders: item["66fb50a4f6b64e676c4782ee"].savings_amount ? moneyFormatter.format(item["66fb50a4f6b64e676c4782ee"].savings_amount) : '',
            greenBuilders: item["66dd1ee77ca40bfb851e389d"].savings_amount ? moneyFormatter.format(item["66dd1ee77ca40bfb851e389d"].savings_amount) : '',
            }))
            return data
    }

    return (
        <div className="container max-w-[1200px] overflow-x-hidden">
            <div className={`flex ${isEditing ? "flex-row" : "flex-col"}`}>
                <div className={` ${isEditing ? "w-2/3" : "w-full"} `}>
                    <br/>
                    <CardHeader>
                        <CardTitle>
                            <div className="flex justify-between">
                                <div>Value Engineering</div>
                                <div className="flex gap-3">
                                <CopyButton2 dataTransform={formatDataToExport} />
                                    <Button variant="outline"
                                            onClick={onSuggestVE}>
                                        Suggest VE
                                    </Button>
                                    {/*<Button variant="outline"*/}
                                    {/*    //    onClick={(e) => {setShowDetail((v) => !v)}}*/}
                                    {/*        onClick={onShowDetail}*/}
                                    {/*>*/}
                                    {/*    Show Detail*/}
                                    {/*</Button>*/}
                                </div>
                            </div>
                        </CardTitle>
                    </CardHeader>
                    {matItems &&
                        <div className="max-w-full text-sm">
                            <ScrollTable
                                columns={tableCols}
                                data={matItems.rows}
                                maxHeight="h-[calc(100vh-250px)]"/>
                        </div>
                    }
                </div>
                {isEditing && (
                    <div className="w-1/3 ml-4">
                        <div className="flex justify-end mb-[20px]">
                            <Button onClick={onCloseVE}>Close</Button>
                        </div>

                        <VeItemForm
                            suggestion_id={suggestionId}
                            price_id={priceId}
                            setItemId={setPriceId}
                            setSuggestionId={setSuggestionId}
                            suggestionPrice={editingItem}
                            updateItem={onUpdateListItem}
                            insertItem={insertListItem}
                            hideItem={hideListItem}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}

export default DeveloperVeView;
